import { HeartBank } from "../../../managers/HeartBank.ts";
import { Stamps } from "../../../models/Stamps.ts";
import template from "./hb-factory.mjs";
import { render } from "lit-html";
import { B, downloadFile, fetchBlob } from "../../global.ts";
import QRCode from "qrcode";

import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

export class HbFactory extends HTMLElement {
    private heartbank = new HeartBank();
    private stamps = new Stamps();
    private modal: ModalInterface;

    private factory: HTMLElement;
    private bstamps: HTMLUListElement;
    private createBtn: HTMLButtonElement;
    private message: HTMLParagraphElement;
    private total: HTMLSpanElement;

    constructor() {
        super();
        render(template(null), this);

        this.factory = this.querySelector("#hb-factory")!;
        this.bstamps = this.querySelector("#hb-factory-stamps")!;
        this.createBtn = this.querySelector("#hb-factory-create")!;
        this.message = this.querySelector("#hb-factory-message")!;
        this.total = this.querySelector("#hb-factory-total")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40",
            closable: true,
            onHide: () => {
                //window.location.hash = "#";
            },
            onShow: () => {
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        const instanceOptions: InstanceOptions = {
            id: "hb-factory",
            override: true
        };

        this.modal = new Modal(this.factory, modalOptions, instanceOptions);
    }

    connectedCallback() {
        this.createBtn.addEventListener("click", this.create.bind(this));

        this.heartbank.addAuthStateListener((user) => {
            if (this.heartbank.isMonk()) this.render();
        });
    }

    show() {
        if (this.heartbank.isMonk()) {
            this.render();
            this.modal.show();
        }
    }

    hide() {
        this.modal.hide();
    }

    private async create() {
        this.createBtn.disabled = true;
        await this.stamps.create();
        await this.render();
        this.createBtn.disabled = false;
    }

    private async render() {
        const stamps = await this.stamps.getAll();
        if (stamps.length === 0) {
            this.bstamps.innerHTML = "You have not yet created any B-Stamps";
            this.bstamps.style.color = "gray";
        } else {
            let net = 0;
            this.bstamps.innerHTML = "";
            this.bstamps.style.color = "";
            stamps.forEach((stamp, index) => {
                const li = document.createElement("li");
                li.classList.add(
                    "flex",
                    "items-center",
                    "justify-between",
                    index === this.stamps.total - 1 ? "mb-0" : "mb-4",
                    "bg-white",
                    "dark:bg-gray-800",
                    "border",
                    "border-gray-200",
                    "dark:border-gray-700",
                    "shadow-sm",
                    "rounded-lg",
                    "p-4",
                    "text-lg",
                    "font-semibold",
                    "text-gray-900",
                    "dark:text-white"
                );

                const copyBtn = document.createElement("button");
                copyBtn.classList.add("me-4");
                copyBtn.innerHTML = "# " + stamp.index;
                copyBtn.onclick = (event) => {
                    this.copy(
                        event.target as HTMLButtonElement,
                        stamp.id!,
                        stamp.index!
                    );
                };

                const downloadBtn = document.createElement("button");
                downloadBtn.classList.add("me-5");
                downloadBtn.innerHTML = "📥";
                downloadBtn.onclick = (event) => {
                    this.download(
                        event.target as HTMLButtonElement,
                        stamp.id!,
                        stamp.index!
                    );
                };

                const printBtn = document.createElement("button");
                printBtn.innerHTML = "🖨️";
                printBtn.onclick = (event) => {
                    this.print(
                        event.target as HTMLButtonElement,
                        stamp.id!,
                        stamp.index!
                    );
                };

                const aside = document.createElement("aside");
                aside.append(copyBtn, downloadBtn, printBtn);

                const total = (stamp.scans || 0) + (stamp.digs || 0);
                net += total;

                const a = document.createElement("a");
                a.classList.add("font-normal", "text-sm");
                a.style.color = stamp.uid ? "green" : "";
                a.innerHTML = `${total} Scans`;
                a.href = `${B}/x/${stamp.id!}`;
                a.target = "_blank";

                const printedBtn0 = document.createElement("button");
                printedBtn0.classList.toggle("hidden", !!stamp.isPrinted);
                printedBtn0.textContent = "⬜️";
                printedBtn0.onclick = (event) => {
                    this.updateStatus(
                        event.target as HTMLButtonElement,
                        stamp.id!,
                        true
                    );
                };

                const printedBtn1 = document.createElement("button");
                printedBtn1.classList.toggle("hidden", !stamp.isPrinted);
                printedBtn1.textContent = "✅";
                printedBtn1.onclick = (event) => {
                    this.updateStatus(
                        event.target as HTMLButtonElement,
                        stamp.id!,
                        false
                    );
                };

                li.append(aside, a, printedBtn0, printedBtn1);
                this.bstamps.appendChild(li);
            });

            this.renderTotal(net);
            //this.renderTotal2(stamps.length);
        }
    }

    private async renderTotal(net: number) {
        this.total.textContent = net + " Total";
    }

    // not used
    private async renderTotal2(count: number = 0) {
        this.total.textContent =
            (count || (await this.stamps.count())) + " Total";
    }

    private async updateStatus(
        button: HTMLButtonElement,
        id: string,
        status: boolean
    ) {
        button.disabled = true;
        await this.stamps.updateStatus(id, status);
        button.disabled = false;
        this.render();
    }

    private async copy(button: HTMLButtonElement, id: string, index: number) {
        button.disabled = true;
        const dataURL = await this.createQrCode(id);
        const blob = await fetchBlob(dataURL);
        const clipboardItem = new ClipboardItem({ [blob.type]: blob });
        await navigator.clipboard.write([clipboardItem]);
        this.message.textContent = `#${index} has been copied!`;
        this.message.style.color = "green";
        button.disabled = false;
    }

    private async download(
        button: HTMLButtonElement,
        id: string,
        index: number
    ) {
        button.disabled = true;
        const dataURL = await this.createQrCode(id);
        await downloadFile(index + ".B.stamp.png", dataURL);
        button.disabled = false;
    }

    private async print(button: HTMLButtonElement, id: string, index: number) {
        button.disabled = true;
        await navigator.clipboard.writeText(`${B}/x/${id}`);
        this.message.textContent = `#${index} has been copied!`;
        this.message.style.color = "green";
        button.disabled = false;
    }

    // not used
    private async print2(button: HTMLButtonElement, id: string): Promise<void> {
        const url = await this.createQrCode(id);
        return new Promise((resolve, reject) => {
            const printWindow = window.open("", "_blank");
            if (!printWindow) {
                reject(new Error("Failed to open print window"));
                return;
            }

            printWindow.document.write(`
            <html>
                <head>
                    <title>HeartBank® Chest</title>
                    <style>
                        body, html {
                            margin: 0;
                            padding: 0;
                            background: skyblue;
                        }
                        body {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            width: 100vw;
                            height: 100vh;
                        }
                        img {
                            max-width: 100%;
                            max-height: 100%;
                        }
                    </style>
                </head>
                <body>
                    <img src="${url}" onload="window.focus();window.print();" onafterprint="window.close();" />
                </body>
            </html>
        `);

            printWindow.document.close();
            resolve();
        });
    }

    private async createQrCode(id: string): Promise<string> {
        return new Promise((resolve, reject) => {
            QRCode.toDataURL(
                `${B}/x/${id}`,
                {
                    width: 1000, // resolution
                    errorCorrectionLevel: "L",
                    type: "image/png",
                    margin: 0,
                    color: {
                        dark: "#000",
                        light: "#fff" // white background
                    },
                    version: undefined // smallest version
                },
                (error: Error | null | undefined, url: string) => {
                    if (error) {
                        reject(
                            new Error(
                                `Error generating QR Code: ${error.message}`
                            )
                        );
                    } else {
                        resolve(url);
                    }
                }
            );
        });
    }
}
